import React, { useState } from 'react';
import { CaretDown, Phone } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
const faqs = [{
  q: 'How is Knight different from a regular bookkeeper?',
  a: 'A typical bookkeeper enters transactions, runs payroll and disappears until BAS time. Knight does all of that, but every engagement is supervised by an IPA-recognised manager who thinks like a CFO. That means monthly management reports, proactive cash-flow flags and strategic context baked into the day-to-day work, on a fixed monthly fee. You are not just paying for clean books, you are paying for someone watching the numbers the way a CFO would.'
}, {
  q: 'Is my financial data safe?',
  a: 'Yes. We sign a confidentiality agreement before any data is shared. Our practices are aligned with the Australian Cyber Security Centre Essential Eight framework. All client data sits on Australian-region cloud infrastructure (Xero AU, MYOB AU, Microsoft Azure Australia East), with daily geo-redundant backups and a secure handover process.'
}, {
  q: 'Do I have to change my accounting software?',
  a: 'No. We work natively inside your existing Xero, MYOB or QuickBooks Online, supported by tools like Dext and Employment Hero. The transition is straightforward and requires no software switch.'
}, {
  q: 'I already have a bookkeeper or in-house person.',
  a: 'We do this every week. We can complement your existing internal team, or coordinate a clean handover from your current bookkeeper with zero disruption. We run the first month in parallel where it makes sense, so you do not have to manage the awkward conversations.'
}, {
  q: 'Am I locked into a contract?',
  a: 'No lock-in contracts. We charge a fixed monthly fee, and you can cancel anytime on standard notice. We would rather earn the work each month than tie you in.'
}, {
  q: 'What does it actually cost?',
  a: 'A fixed monthly fee, scoped specifically to your business. There are no startup costs and no surprise hourly add-ons. After our free 30-minute consultation, you receive a written proposal within 48 hours detailing exactly what you pay.'
}, {
  q: 'How quickly can you start?',
  a: 'Most engagements begin within 1 to 2 weeks of accepting the proposal. Onboarding happens inside your existing software, typically within days.'
}, {
  q: 'Are you actually Australian?',
  a: 'Yes. We are 100% Australian-owned and operated, with offices in Perth and Sydney. You will always deal with an Australian-supervised team, not an offshore call centre.'
}, {
  q: 'When should I consider a Virtual CFO?',
  a: 'Consider Virtual CFO support when the business is growing quickly, facing complex financial decisions, or needs budgeting, forecasting, risk management and strategic financial guidance. Without yet needing a full-time CFO on payroll.'
}, {
  q: 'How fast will I get answers on day-to-day questions?',
  a: 'Our typical response time on client queries is under 4 business hours during our standard support window (5 days a week, 52 weeks a year).'
}, {
  q: 'What does outsourced bookkeeping include?',
  a: 'Bank reconciliation, financial statement reconciliation, double-entry bookkeeping, accounts payable and receivable, BAS compliance, general ledger maintenance, and monthly management reporting.'
}, {
  q: 'What does outsourced payroll include?',
  a: 'Wage and salary calculations, superannuation contributions, payroll reporting, Single Touch Payroll (STP) compliance, award interpretation, overtime and leave management, and secure employee data handling.'
}];
export function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  return <section id="faq" className="py-16 lg:py-24 bg-off-white" data-id="element-87">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-88">
        <div className="text-center mb-12" data-id="element-89">
          <p className="font-mono text-[11px] tracking-wider uppercase text-knight-orange mb-3" data-id="element-90">
            Answers before you ask
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-charcoal leading-tight" data-id="element-91">
            Frequently asked questions
          </h2>
        </div>

        <div className="space-y-2.5 mb-10" data-id="element-92">
          {faqs.map((faq, i) => <div key={i} className="border border-hairline rounded-md overflow-hidden bg-white" data-id="element-93">
              <button className="w-full px-6 py-5 text-left flex justify-between items-center focus:outline-none focus:ring-2 focus:ring-knight-orange/40 hover:bg-off-white transition-colors duration-300 ease-out-expo" onClick={() => setOpenIndex(openIndex === i ? null : i)} data-id="element-94">
                <span className="font-semibold text-charcoal pr-4" data-id="element-95">
                  {faq.q}
                </span>
                <CaretDown size={18} weight="regular" className={`text-knight-orange transition-transform duration-500 ease-out-expo flex-shrink-0 ${openIndex === i ? 'rotate-180' : ''}`} data-id="element-96" />
              </button>
              <AnimatePresence initial={false} data-id="element-97">
                {openIndex === i && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.4,
              ease: [0.16, 1, 0.3, 1]
            }} className="overflow-hidden" data-id="element-98">
                    <div className="px-6 pb-5 pt-1 text-gray-700 leading-relaxed border-t border-hairline" data-id="element-99">
                      {faq.a}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>)}
        </div>

        <div className="bg-white rounded-md border border-hairline p-6 text-center" data-id="element-100">
          <p className="text-charcoal font-medium mb-3" data-id="element-101">
            Still have a question we have not answered?
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-102">
            <a href="tel:+61863179998" className="inline-flex items-center justify-center px-5 py-3 border border-hairline text-charcoal font-semibold rounded-md hover:bg-off-white transition-colors text-sm" data-id="element-103">
              <Phone size={14} weight="regular" className="mr-2" data-id="element-104" />
              <span className="font-mono" data-id="element-105">+61 8 6317 9998</span>
            </a>
            <a href="#lead" className="inline-flex items-center justify-center px-5 py-3 bg-knight-orange text-white font-semibold rounded-md hover:bg-knight-orange-hover transition-colors text-sm" data-id="element-106">
              Ask in a free consultation
            </a>
          </div>
        </div>
      </div>
    </section>;
}